import { setSeconds } from "date-fns";
import { useEffect, useMemo } from "react";
import { Polyline, useMap } from "react-leaflet";
import { useApiResource } from "../../../../hooks/useApiResource";
import { IFlightMarker } from "../../../../models/airports/AirportConfig";
import {
  AIRPORT_CODE_PARAMETER,
  ICAO_ADDRESS,
} from "../../../../utils/common/Constants";
import {
  convertToUTC,
  formatDateForApiPayload,
} from "../../../../utils/common/DateHelperFunctions";
import FlightMarker from "./FlightMarker";

export interface ISelectedFlightMarker {
  selectedFlightMarkerDetails: IFlightMarker;
  airportCode: string;
  pane?: string;
  handleMarkerClick: (flightID: string, docId: string) => void;
}

const polylineOptions = {
  color: "#9d4edd",
  weight: 4,
  opacity: 0.9,
  lineJoin: "round" as CanvasLineJoin,
  lineCap: "round" as CanvasLineCap,
  smoothFactor: 2,
};

const flightTrackingPathURL = process.env.REACT_APP_API_FLIGHT_TRACKING_PATH;
const baseURL = process.env.REACT_APP_API_BASE_URL;
const fligthTrackingPathUrl = `${baseURL}${flightTrackingPathURL}`;

const SelectedFlightMarker: React.FC<ISelectedFlightMarker> = ({
  selectedFlightMarkerDetails,
  airportCode,
  pane = "",
  handleMarkerClick,
}) => {
  const map = useMap();

  const trackingUrlWithParams = selectedFlightMarkerDetails.id
    ? fligthTrackingPathUrl
        ?.replace(AIRPORT_CODE_PARAMETER, airportCode)
        .replace(ICAO_ADDRESS, selectedFlightMarkerDetails.id)
    : "";

  const currentTime = formatDateForApiPayload(
    setSeconds(convertToUTC(new Date()), 0)
  );

  const payload = {
    end: currentTime,
    start: selectedFlightMarkerDetails.bestOffBlockTime,
    callSign: selectedFlightMarkerDetails.callSign,
    flightNumber: selectedFlightMarkerDetails.flightNumber,
    limit: 500,
  };

  const { data: flightTrackingData, isLoading: flightTrackingLoading } =
    useApiResource({
      url: trackingUrlWithParams,
      queryKey: [
        "airportTracking",
        airportCode,
        JSON.stringify(selectedFlightMarkerDetails),
      ],
      fetchOnPost: true,
      fetchPayload: payload,
    });

  const flightPath: [number, number][] = flightTrackingData
    ? flightTrackingData?.data_points?.map((flight: any) => [
        flight.latitude,
        flight.longitude,
      ])
    : [];

  const selectedFlightMarkerPosition: [number, number] = [
    selectedFlightMarkerDetails.latitude,
    selectedFlightMarkerDetails.longitude,
  ];

  const flightPosition = useMemo(
    () =>
      flightTrackingLoading
        ? selectedFlightMarkerPosition
        : flightPath.at(-1) || selectedFlightMarkerPosition,
    [flightTrackingLoading, flightPath, selectedFlightMarkerPosition]
  );

  // Center the map on the flightPosition
  useEffect(() => {
    if (flightPosition) {
      map.flyTo(flightPosition, map.getZoom(), {
        animate: true,
        duration: 1,
      });
    }
  }, [flightPosition[0], flightPosition[1], map]);

  return (
    <>
      <FlightMarker
        key={selectedFlightMarkerDetails.id}
        airportCode={airportCode}
        id={selectedFlightMarkerDetails.id}
        flightNumber={selectedFlightMarkerDetails.flightNumber}
        direction={selectedFlightMarkerDetails.direction}
        position={flightPosition}
        flightStatus={selectedFlightMarkerDetails.status}
        legType={selectedFlightMarkerDetails.legType}
        docID={selectedFlightMarkerDetails.docId}
        selectedFlight={true}
        pane={pane}
        acType={selectedFlightMarkerDetails.aircraftType}
        acReg={selectedFlightMarkerDetails.aircraftRegistration}
        onBlockTime={selectedFlightMarkerDetails.bestInBlockTime}
        offBlockTime={selectedFlightMarkerDetails.bestOffBlockTime}
        updateMarkerId={handleMarkerClick}
      />
      {!flightTrackingLoading && (
        <Polyline positions={flightPath} pathOptions={polylineOptions} />
      )}
    </>
  );
};

export default SelectedFlightMarker;
